import { pick } from 'ramda'
import React from 'react'
import { Box, Button, Form, useFormContext } from '../../index'
import type { PillColor } from '../../Pill'

const stylesFormDebugger = {
  backgroundColor: '#444',
  color: '#f1f1f1',
  fontSize: '0.8rem',
}

export function FormDebugger(props: any = {}) {
  const fields = useFormContext()

  let record = {}
  if (!Object.keys(props).length) {
    record = fields
  } else {
    record = pick(Object.keys(props), fields)
  }

  return (
    <Box flex="1" padding="md" margin="lg none" style={stylesFormDebugger}>
      <pre style={{ whiteSpace: 'break-spaces' }}>
        {JSON.stringify(record, null, 2)}
      </pre>
    </Box>
  )
}

interface SingleFormProps {
  component?: any
  debug?: boolean
  initialValue?: any
  variant?: React.ComponentProps<typeof Form>['variant']
  view?: React.ComponentProps<typeof Form>['view']
  useLabelAsInlineLabel?: boolean
  useDescriptionAsInlineDescription?: boolean
}

/**
 * Takes a Form Field component, e.g. Form.Select, and creates
 * a full Form of one view and variant. Empty value examples,
 * examples with values, various text or state formats.
 */
const SingleForm = ({
  component: FieldComponent = Form.Field,
  debug,
  initialValue,
  view,
  useLabelAsInlineLabel,
  useDescriptionAsInlineDescription,
  ...props
}: SingleFormProps) => {
  // removes colStart from DOM for traditional variant
  const columnOne = { colStart: 1 }
  const columnTwo = { colStart: 7 }
  return (
    <Form
      enableReinitialize
      initialValues={{
        value: initialValue,
      }}
      view={view}
      onSubmit={(values) => alert(JSON.stringify(values, null, 2))}
    >
      <Form.Form>
        {debug && <FormDebugger />}
        <Form.Row>
          <FieldComponent
            name="empty"
            {...(useLabelAsInlineLabel
              ? {
                  inlineLabel: 'Empty Text',
                }
              : {
                  label: 'Empty Text',
                })}
            {...(useDescriptionAsInlineDescription
              ? {
                  inlineDescription: 'Empty Description',
                }
              : {
                  description: 'Empty Description',
                })}
            {...columnOne}
            {...props}
          />
          <FieldComponent
            name="value"
            {...(useLabelAsInlineLabel
              ? {
                  inlineLabel: 'Initial Value Text',
                }
              : {
                  label: 'Initial Value Text',
                })}
            {...(useDescriptionAsInlineDescription
              ? {
                  inlineDescription: 'Initial Value Description',
                }
              : {
                  description: 'Initial Value Description',
                })}
            {...columnTwo}
            {...props}
          />
        </Form.Row>
        <Form.Row>
          <FieldComponent
            name="disabled-empty"
            disabled
            {...(useLabelAsInlineLabel
              ? {
                  inlineLabel: 'Disabled Empty Text',
                }
              : {
                  label: 'Disabled Empty Text',
                })}
            {...(useDescriptionAsInlineDescription
              ? {
                  inlineDescription: 'Disabled Empty Description',
                }
              : {
                  description: 'Disabled Empty Description',
                })}
            {...columnOne}
            {...props}
          />
          <FieldComponent
            name="disabled-value"
            disabled
            {...(useLabelAsInlineLabel
              ? {
                  inlineLabel: 'Disabled Initial Value Text',
                }
              : {
                  label: 'Disabled Initial Value Text',
                })}
            {...(useDescriptionAsInlineDescription
              ? {
                  inlineDescription: 'Disabled Initial Description',
                }
              : {
                  description: 'Disabled Initial Description',
                })}
            {...columnTwo}
            {...props}
          />
        </Form.Row>
        <Form.Row>
          <FieldComponent
            name="error-value"
            error="Some error message that should be read red"
            {...(useLabelAsInlineLabel
              ? {
                  inlineLabel: 'Error Message Initial Value Text',
                }
              : {
                  label: 'Error Message Initial Value Text',
                })}
            {...(useDescriptionAsInlineDescription
              ? {
                  inlineDescription: 'Error Message Initial Description',
                }
              : {
                  description: 'Error Message Initial Description',
                })}
            {...columnOne}
            {...props}
          />
        </Form.Row>
      </Form.Form>
    </Form>
  )
}

interface DemoFormProps {
  view?: React.ComponentProps<typeof Form>['view']
  enableConfirmNavigation?: React.ComponentProps<
    typeof Form
  >['enableConfirmNavigation']
}
export const DemoForm = ({
  view = 'create',
  enableConfirmNavigation = true,
}: DemoFormProps) => {
  return (
    <Form
      view={view}
      onSubmit={(...args) => {
        alert(JSON.stringify(args[0], null, 2))

        // Necessary to return success to notify the client the data
        // is saved on the server and this `form` is pristine once again.
        return Promise.resolve()
      }}
      enableConfirmNavigation={enableConfirmNavigation}
    >
      <Form.Form>
        <Form.Row>
          <Form.Text name="text" label="Text" colStart={1} />
          <Form.Number
            name="number"
            label="Number"
            required
            tooltip="An example tooltip for any field, this value goes directly to the Tooltip overlay prop"
            colStart={7}
          />
        </Form.Row>
        <Form.Row>
          <Form.Select
            name="select"
            label="Select"
            options={formSelectOptions}
            colStart={1}
          />
          <Form.MultiSelect
            name="multiselect"
            label="MultiSelect"
            options={formSelectOptions}
            colStart={7}
          />
        </Form.Row>
        <Form.Row>
          <Form.Select
            name="select"
            label="Select with Groups"
            getGroup={(option) => option.groupAssociation}
            groupGetId={(group) => group.uuid}
            groupGetLabel={(group) => group.displayName}
            options={formSelectOptions}
            optgroups={formSelectGroupOptions}
          />
          <Form.PillSelect
            name="pillselect"
            label="Status"
            options={formPillSelectOptions}
            colStart={7}
          />
        </Form.Row>
        <Form.Row>
          <Form.TieredSelect
            name="tieredselect"
            label="Tiered Select"
            options={formTieredSelectOptions}
            colStart={1}
          />
          <Form.GroupSelect
            name="groupselect"
            label="Group Select for Distribution"
            options={formGroupSelectOptions}
            groups={formGroupSelectGroups}
            colStart={7}
          />
        </Form.Row>
        <Box textAlign="center" marginTop="sm" marginBottom="xl">
          <Button variant="secondary" type="reset">
            Reset
          </Button>{' '}
          <Button type="submit">Save</Button>
        </Box>
      </Form.Form>
    </Form>
  )
}

/** All variations for a field. views, with values, no values, text variations, etc.
 * @example
 *  <BaseCRU
      component={Form.Number}
      initialValue={100}
    />
 */
export function BaseCRU<Element extends React.JSXElementConstructor<any>>(
  props: Omit<SingleFormProps, 'component'> & { component: Element } & Omit<
      React.ComponentProps<Element>,
      'name'
    >
) {
  return (
    <>
      <h1>Standard</h1>
      <h2>Create</h2>
      <SingleForm view="create" {...props} />
      <h2>Read</h2>
      <SingleForm view="read" {...props} />
      <h2>Update</h2>
      <SingleForm view="update" {...props} />
    </>
  )
}

export const formGroupSelectOptions = [
  { id: 5139977, label: 'Cat' },
  { id: 1279548, label: 'Cow' },
  { id: 1690, label: 'Duck' },
  { id: 3116635, label: 'Crocodile' },
  { id: 1037291, label: 'Dog' },
  { id: 1987946, label: 'Elephant' },
  { id: 1665038, label: 'Fox' },
  { id: 10338, label: 'Frog' },
  { id: 4177019, label: 'Giraffe' },
  { id: 5136274, label: 'Lion' },
  { id: 1819065, label: 'Owl' },
  { id: 3733119, label: 'Parrot' },
  { id: 2142072, label: 'Pig' },
  { id: 1360365, label: 'Wolf' },
]

export const formGroupSelectGroups = [
  {
    id: 180604,
    label: 'Group #1',
    options: [5139977, 1037291, 1279548, 5136274],
  },
  { id: 183915, label: 'Group #2', options: [1037291, 1279548] },
  { id: 187138, label: 'Group #3', options: [1665038, 10338] },
  { id: 127496, label: 'Group #4', options: [3116635, 1819065] },
  { id: 126261, label: 'Group #5', options: [1690] },
].map((group) => ({
  ...group,
  options: group.options.map((groupOpt) =>
    formGroupSelectOptions.find((option) => option.id === groupOpt)
  ),
}))

export const formTieredSelectOptions = [
  {
    id: 1,
    groupId: null,
    nextGroupId: 10,
    label: 'Apple Farm',
  },
  {
    id: 3,
    groupId: null,
    nextGroupId: 30,
    label: 'Sports R Us',
  },
  {
    id: 4,
    groupId: null,
    nextGroupId: 30,
    label: 'Sail It Again Ports',
  },
  {
    id: 5,
    groupId: null,
    nextGroupId: 30,
    label: 'Dublix',
  },
  {
    id: 6,
    groupId: null,
    nextGroupId: 30,
    label: 'The Hardees Boys',
  },
  {
    id: 7,
    groupId: null,
    nextGroupId: 30,
    label: 'Carls Senior',
  },
  {
    id: 8,
    groupId: null,
    nextGroupId: 30,
    label: 'Tar J',
  },
  {
    id: 9,
    groupId: null,
    nextGroupId: 30,
    label: 'Innovation Station',
  },
  {
    id: 10,
    groupId: null,
    nextGroupId: 30,
    label: 'Innovation Nation',
  },
  {
    id: 11,
    groupId: null,
    nextGroupId: 30,
    label: 'Global Studios',
  },
  {
    id: 12,
    groupId: null,
    nextGroupId: 30,
    label: "Ralphie's",
  },
  {
    id: 13,
    groupId: null,
    nextGroupId: 30,
    label: 'Humble State University',
  },
  {
    id: 14,
    groupId: null,
    nextGroupId: 30,
    label: 'Test Driven Devolvement',
  },
  {
    id: 15,
    groupId: null,
    nextGroupId: 30,
    label: 'The Drilling Company',
  },
  {
    id: 16,
    groupId: null,
    nextGroupId: 30,
    label:
      'Wrapping text that is very long and will definitely wrap onto two or more lines',
  },
  {
    id: 17,
    groupId: 10,
    nextGroupId: 100,
    label: 'Granny Smith',
  },
  {
    id: 18,
    groupId: 10,
    nextGroupId: 101,
    label: 'Gala',
  },
  {
    id: 21,
    groupId: 30,
    nextGroupId: 300,
    label: 'Project Basketball',
  },
  {
    id: 22,
    groupId: 30,
    nextGroupId: 301,
    label: 'Table Tennis',
  },
  {
    id: 23,
    groupId: 30,
    label: 'Table Ten',
  },
  {
    id: 24,
    groupId: 100,
    label: 'Michigan',
  },
  {
    id: 25,
    groupId: 101,
    label: 'Washington',
  },
  {
    id: 26,
    groupId: 200,
    label: 'Georgia',
  },
  {
    id: 27,
    groupId: 201,
    label: 'California',
  },
  {
    id: 28,
    groupId: 300,
    label: 'Toronto',
  },
  {
    id: 29,
    groupId: 301,
    nextGroupId: 302,
    label: 'Court Complex Uno',
  },
  {
    id: 30,
    groupId: 302,
    nextGroupId: 303,
    label: 'Wrap',
  },
  {
    id: 31,
    groupId: 303,
    nextGroupId: 304,
    label: 'Wrap',
  },
  {
    id: 32,
    groupId: 304,
    nextGroupId: 305,
    label: 'Wrap',
  },
  {
    id: 33,
    groupId: 305,
    nextGroupId: 306,
    label: 'Wrap',
  },
  {
    id: 34,
    groupId: 306,
    nextGroupId: 307,
    label: 'Wrap',
  },
  {
    id: 35,
    groupId: 307,
    label: 'Wrap',
  },
]

export const formSelectOptions = [
  { id: 1, label: 'Option Example Uno', groupAssociation: 1 },
  { id: 2, label: 'Item Deux 2', groupAssociation: 2 },
  { id: 3, label: 'Three Three Three', groupAssociation: 1 },
  {
    id: 4,
    label: 'Next Item In List Is Design System Design System',
    groupAssociation: 2,
  },
  { id: 5, label: '5 Entries', groupAssociation: 2 },
  {
    id: 6,
    label:
      'Another Option With A Longer Amount of Important Information To Read',
    groupAssociation: 3,
  },
]

export const formSelectGroupOptions = [
  { uuid: 1, displayName: 'Group 1' },
  {
    uuid: 2,
    displayName: 'Group Having a Longer Group Label, Group Two Is That Group',
  },
  { uuid: 3, displayName: 'Group 3' },
]

export const formPillSelectOptions: {
  id: string
  label: string
  color: PillColor
}[] = [
  { id: '1', label: 'Questionable', color: 'yellow' },
  { id: '2', label: 'False', color: 'red' },
  { id: '3', label: 'The Sky Is Blue', color: 'blue' },
  { id: '4', label: 'Pending Resolution', color: 'gray' },
  {
    id: '5',
    label: 'A very long STATUS that has to wrap cause it is so long',
    color: 'green',
  },
]

export const formInitialValues = {
  text: 'Hello String',
  number: 123456.789,
  checkbox: true,
  checkboxes: [formSelectOptions[1], formSelectOptions[2]],
  currency: 1200300.55,
  date: new Date(2020, 9, 31, 14, 30, 42), // Halloween, a Saturday at 2:30 PM
  textarea: 'Hello\nWorld  !!\n\n    Hello    World',
  select: formSelectOptions[1],
  multiselect: [
    formSelectOptions[1],
    formSelectOptions[3],
    formSelectOptions[0],
  ],
  pillselect: formPillSelectOptions[0],
  groupselect: [formGroupSelectOptions[0], formGroupSelectOptions[2]],
  tieredselect: [
    {
      id: 4,
      groupId: null,
      nextGroupId: 30,
      label: 'Sail It Again Ports',
    },
    {
      id: 22,
      groupId: 30,
      nextGroupId: 301,
      label: 'Table Tennis',
    },
    {
      id: 29,
      groupId: 301,
      nextGroupId: 302,
      label: 'Court Complex Uno',
    },
  ],
  radiobuttons: formSelectOptions[5],
  richtext:
    '<p><span style="font-size: 18pt; background-color: #00ff00;">Rich Text Is <strong>Bold</strong></span></p> ',
}
